import { useState, useRef, useCallback } from "react";

type FacingMode = "user" | "environment";

interface UseCameraCaptureReturn {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isStreaming: boolean;
  facingMode: FacingMode;
  error: string | null;
  startCamera: () => Promise<void>;
  stopCamera: () => void;
  capturePhoto: () => File | null;
  switchCamera: () => Promise<void>;
}

export function useCameraCapture(): UseCameraCaptureReturn {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [isStreaming, setIsStreaming] = useState(false);
  const [facingMode, setFacingMode] = useState<FacingMode>("environment");
  const [error, setError] = useState<string | null>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
  }, []);

  const startCamera = useCallback(
    async (mode: FacingMode = facingMode) => {
      setError(null);

      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }

        const constraints: MediaStreamConstraints = {
          video: {
            facingMode: mode,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        setIsStreaming(true);
      } catch (err) {
        const message =
          err instanceof DOMException && err.name === "NotAllowedError"
            ? "Permiso de cámara denegado. Habilítalo en la configuración del navegador."
            : "No se pudo acceder a la cámara. Verifica que esté disponible.";
        setError(message);
        setIsStreaming(false);
      }
    },
    [facingMode],
  );

  const switchCamera = useCallback(async () => {
    const next: FacingMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(next);
    await startCamera(next);
  }, [facingMode, startCamera]);

  const capturePhoto = useCallback((): File | null => {
    const video = videoRef.current;
    if (!video || !isStreaming) return null;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // Mirror the image if using front camera
    if (facingMode === "user") {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    let file: File | null = null;

    canvas.toBlob(
      (blob) => {
        if (blob) {
          file = new File([blob], `foto-${timestamp}.jpg`, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
        }
      },
      "image/jpeg",
      0.92,
    );

    // toBlob is async but we need the file synchronously for the canvas data URL fallback
    if (!file) {
      const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
      const byteString = atob(dataUrl.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: "image/jpeg" });
      file = new File([blob], `foto-${timestamp}.jpg`, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    }

    return file;
  }, [isStreaming, facingMode]);

  return {
    videoRef,
    isStreaming,
    facingMode,
    error,
    startCamera,
    stopCamera,
    capturePhoto,
    switchCamera,
  };
}
